import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Check, Star, Lock, ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

// Dedicated pricing page (/pricing) — separate from the landing anchor
// (#pricing). Having its own URL with a proper H1 helps SEO capture the
// "planes y precios" / "planes chatbot whatsapp" keyword cluster.
export default function PricingPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://acbothub.com/' },
      { '@type': 'ListItem', position: 2, name: 'Planes y precios', item: 'https://acbothub.com/pricing' },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <SEO
        title="Planes y Precios — ACBotHub"
        description="Planes desde $24/mes. Bots de WhatsApp con IA, kits por industria y prueba gratis. Sin contratos, cancela cuando quieras."
        path="/pricing"
        jsonLd={jsonLd}
      />

      {/* Navbar */}
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-nav-home">
            <MessageSquare className="w-7 h-7 text-indigo-600" />
            <span className="text-xl font-bold tracking-tight text-gray-950">ACBotHub</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-gray-700 hover:text-gray-950">Iniciar sesión</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md hover:shadow-lg transition-all rounded-lg font-medium px-6 py-2.5">
                Comenzar
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 px-6 max-w-7xl mx-auto">
        <nav className="text-sm text-gray-500 flex items-center gap-2">
          <Link to="/" className="hover:text-gray-800">Inicio</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-800 font-medium">Planes y precios</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="px-6 pb-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-950 mb-4">
            Planes y precios de ACBotHub
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Elige el plan que necesitas. Todos incluyen prueba gratis. Sin contratos, cancela cuando quieras.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-12 px-6 bg-gray-50" data-testid="pricing-plans">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
            {/* BÁSICO */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="plan-basic">
              <h2 className="text-lg font-semibold text-gray-950 mb-1">Básico</h2>
              <p className="text-sm text-gray-500 mb-6">Para emprendedores</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-950">$24</span>
                <span className="text-gray-500 text-sm">/mes</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {[
                  '1 asistente WhatsApp con IA',
                  'Dashboard de gestión',
                  'Conexión WhatsApp vía QR',
                  'Widget embebible en tu web',
                  'Historial 7 días',
                  'Soporte por email',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
                <li className="flex items-start gap-2.5 text-sm text-gray-500">
                  <Lock className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <span>Sin kit de industria</span>
                </li>
              </ul>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 mb-6">
                <p className="text-xs text-indigo-700 font-medium text-center">Prueba 7 días gratis</p>
              </div>
              <Link to="/register">
                <Button variant="outline" data-testid="plan-basic-btn"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-5 font-medium">
                  Empezar prueba gratis
                </Button>
              </Link>
            </div>

            {/* PRO */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow" data-testid="plan-pro">
              <h2 className="text-lg font-semibold text-gray-950 mb-1">Pro</h2>
              <p className="text-sm text-gray-500 mb-6">Para negocios en crecimiento</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-950">$49</span>
                <span className="text-gray-500 text-sm">/mes</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {[
                  '3 asistentes activos',
                  'Historial completo',
                  'Integración Google Calendar',
                  'Reagendamiento automático',
                  'Widget embebible',
                  'Soporte prioritario',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
                <li className="flex items-start gap-2.5 text-sm text-gray-500">
                  <Lock className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                  <span>Sin kit de industria</span>
                </li>
              </ul>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 mb-6">
                <p className="text-xs text-indigo-700 font-medium text-center">Prueba 14 días gratis</p>
              </div>
              <Link to="/register">
                <Button variant="outline" data-testid="plan-pro-btn"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-5 font-medium">
                  Empezar prueba gratis
                </Button>
              </Link>
            </div>

            {/* BUSINESS */}
            <div className="bg-white border-2 border-indigo-600 rounded-2xl p-8 shadow-lg relative" data-testid="plan-business">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-md">
                  <Star className="w-3 h-3 fill-current" />Más popular
                </span>
              </div>
              <h2 className="text-lg font-semibold text-gray-950 mb-1">Business</h2>
              <p className="text-sm text-gray-500 mb-6">Para negocios con kit de industria</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-950">$199</span>
                <span className="text-gray-500 text-sm">/mes</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {[
                  '10 asistentes activos',
                  'Todo lo del plan Pro',
                  'Pasarelas de pago (Flow, Klap, PayPal)',
                  'Carrito abandonado automático',
                  'Widget con branding personalizado',
                  'Soporte VIP + onboarding',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
                <li className="flex items-start gap-2.5 text-sm text-gray-950 font-semibold bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-2">
                  <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                  <span>1 kit de industria incluido (elige al activar)</span>
                </li>
              </ul>
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 mb-6">
                <p className="text-xs text-indigo-700 font-medium text-center">Prueba 14 días gratis · Onboarding incluido</p>
              </div>
              <Link to="/register">
                <Button data-testid="plan-business-btn"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-5 font-medium shadow-md hover:shadow-lg transition-all">
                  Empezar prueba gratis
                </Button>
              </Link>
            </div>
          </div>

          {/* Kits add-on */}
          <div className="max-w-6xl mx-auto mt-14" data-testid="kits-addon-section">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                    <Star className="w-3 h-3" /> Add-on Business
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-950">Kits de industria</h2>
                  <p className="text-sm text-gray-600 mt-1 max-w-2xl">
                    Elige 1 kit incluido en tu plan Business. Los otros 3 aparecen con candado en el dashboard y se activan pagando <strong className="text-gray-950">+$99/mes</strong> cada uno.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { slug: 'garzonbot', name: 'GarzónBot', logo: '/logo_garzonbot.png', desc: 'Restaurantes' },
                  { slug: 'autobot', name: 'AutoBot', emoji: '🚗', desc: 'Concesionarias' },
                  { slug: 'inmobot', name: 'InmoBot', emoji: '🏠', desc: 'Inmobiliarias' },
                  { slug: 'legalbot', name: 'LegalBot', emoji: '⚖️', desc: 'Estudios jurídicos' },
                ].map((k) => (
                  <Link
                    key={k.slug}
                    to={`/kits/${k.slug}`}
                    data-testid={`pricing-kit-card-${k.slug}`}
                    className="group bg-gray-50 hover:bg-white border border-gray-200 hover:border-indigo-300 rounded-xl p-4 transition-all hover:shadow-md"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      {k.logo ? (
                        <img src={k.logo} alt={`${k.name} logo`} className="w-8 h-8 object-contain shrink-0" />
                      ) : (
                        <span className="text-2xl" aria-hidden>{k.emoji}</span>
                      )}
                      <div className="font-semibold text-gray-950 text-sm">{k.name}</div>
                    </div>
                    <div className="text-xs text-gray-500">{k.desc}</div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 group-hover:text-indigo-700">
                      Ver detalles <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Partners note */}
          <div className="max-w-6xl mx-auto mt-6 text-center">
            <p className="text-sm text-gray-600">
              ¿Eres agencia o consultor?{' '}
              <Link to="/register-partner" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                Únete al programa Partners →
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-800">← Volver a la portada</Link>
        </div>
      </footer>
    </div>
  );
}
